import { Controller } from '@hotwired/stimulus';
import { LogicEngine } from 'json-logic-engine';
import { characterGroups } from '../lib/automation_character_groups.js';

// Types select an outlet collection; each controller explicitly exposes its effects.
const OUTLETS = { character_avatar: 'automatableCharacterAvatarOutlets', sound_effect: 'automatableSoundEffectOutlets' };
const ROOT_SELECTOR = '[data-controller~="automation"]';

export default class extends Controller {
  static outlets = ['automatable--character-avatar', 'automatable--sound-effect'];
  static targets = ['logicData'];
  static values = { rules: Array };

  initialize() {
    this.engine = new LogicEngine();
    this.records = new Map();
  }

  connect() {
    this.observer = new MutationObserver(mutations => this.logicDataChanged(mutations));
    this.observer.observe(this.element, { subtree: true, attributes: true, attributeFilter: ['value', 'data-automation-key'] });
    this.schedule();
  }

  disconnect() {
    this.observer?.disconnect();
    this.pending = false;
  }

  rulesValueChanged() { this.schedule(); }
  configure(event) { this.rulesValue = event.detail.rules; }
  automatableCharacterAvatarOutletConnected() { this.schedule(); }
  automatableCharacterAvatarOutletDisconnected() { this.schedule(); }
  automatableSoundEffectOutletConnected() { this.schedule(); }
  automatableSoundEffectOutletDisconnected() { this.schedule(); }

  logicDataTargetConnected(element) {
    this.cacheRecord(element);
    this.schedule();
  }

  logicDataTargetDisconnected(element) {
    this.records.delete(element);
    this.schedule();
  }

  logicDataChanged(mutations) {
    const changed = new Set(mutations.map(mutation => mutation.target).filter(element => this.records.has(element)));
    for (const element of changed) this.cacheRecord(element);
    if (changed.size) this.schedule();
  }

  cacheRecord(element) {
    const key = element.dataset.automationKey;
    const serialized = element.value;
    const previous = this.records.get(element);
    if (previous?.key === key && previous?.serialized === serialized) return;

    let data;
    try {
      data = JSON.parse(serialized);
    } catch {
      // Replace invalid facts instead of retaining stale values from this record.
    }
    this.records.set(element, { key, serialized, data });
  }

  get logicData() {
    const data = Object.create(null);
    for (const { key, data: value } of this.records.values()) {
      const parts = key?.split('.') || [];
      if (!parts.length || parts.some(part => !/^\w+$/.test(part) || ['__proto__', 'constructor', 'prototype'].includes(part))) continue;
      let parent = data;
      for (const part of parts.slice(0, -1)) parent = parent[part] ||= Object.create(null);
      parent[parts.at(-1)] = value;
    }
    data.character_groups = characterGroups(data.characters);
    return data;
  }

  schedule() {
    if (this.pending) return;
    this.pending = true;
    queueMicrotask(() => {
      this.pending = false;
      if (this.element.isConnected) this.evaluate();
    });
  }

  evaluate() {
    // A reveal can request evaluation before the mutation observer's callback.
    for (const mutation of this.observer?.takeRecords() || []) {
      if (this.records.has(mutation.target)) this.cacheRecord(mutation.target);
    }
    const data = this.logicData;
    const outlets = Object.values(OUTLETS).flatMap(property => this[property])
      .filter(outlet => outlet.element.closest(ROOT_SELECTOR) === this.element);

    // A one-character round ends and restarts the same active turn in one
    // transaction. Its persisted sequence distinguishes that from an ordinary
    // morph, a manual round edit, or a Stimulus reconnect.
    const previous = this.element.previousAutomationFacts;
    const restarted = Object.entries(data.characters || {}).filter(([key, character]) => {
      const before = previous?.characters?.[key];
      return before?.active_turn && character.active_turn && character.turn_sequence > before.turn_sequence;
    });
    if (previous?.campaign?.in_combat && data.campaign?.in_combat && restarted.length) {
      const ended = { ...data, campaign: previous.campaign, characters: { ...data.characters } };
      for (const [key, character] of restarted) ended.characters[key] = { ...character, active_turn: false };
      ended.character_groups = characterGroups(ended.characters);
      this.evaluateFacts(ended, outlets);
    }
    this.element.previousAutomationFacts = data;
    this.evaluateFacts(data, outlets);
  }

  evaluateFacts(data, outlets) {
    for (const outlet of outlets) this.invoke(outlet, 'beginEvaluation');

    // Rails supplies position order. Later matching automations replace earlier
    // effects when they change the same property.
    for (const automation of this.rulesValue) {
      if (!automation || typeof automation.automatable_effect !== 'string') continue;
      const [type, effect, extra] = automation.automatable_effect.split('.');
      if (extra !== undefined || !Object.hasOwn(OUTLETS, type)) continue;
      try {
        if (this.engine.run(automation.json_logic_rules, data) !== true) continue;
      } catch {
        continue;
      }
      const outlet = outlets.find(candidate => this[OUTLETS[type]].includes(candidate) && candidate.idValue === automation.automatable_id);
      if (!outlet?.constructor.effects?.includes(effect)) continue;
      try {
        outlet[effect](automation.id);
      } catch {
        // A failed effect must not block the remaining automations.
      }
    }

    for (const outlet of outlets) this.invoke(outlet, 'finishEvaluation');
  }

  invoke(outlet, method) {
    try {
      outlet[method]?.();
    } catch {
      // One disconnected or broken target must not stop the rest of the screen.
    }
  }
}
